import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// Cognito configuration from environment
const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION || 'us-east-1';
const COGNITO_USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // { email, name, accessToken, idToken }
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('me');
    const accessToken = sessionStorage.getItem('accessToken');
    if (stored && accessToken) {
      try {
        setUser(JSON.parse(stored));
      } catch (e) { /* ignore */ }
    }
    setLoading(false);
  }, []);

  const signIn = useCallback(async ({ email, password }) => {
    const api = await import('../api.js');
    const { data } = await api.default.post('/api/logins/signin', { email, password });

    // Handle Cognito challenges
    if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
      const err = new Error('NEW_PASSWORD_REQUIRED');
      err.challenge = 'NEW_PASSWORD_REQUIRED';
      err.session = data.session;
      err.email = data.email;
      throw err;
    }
    if (data.challenge === 'PASSWORD_RESET_REQUIRED') {
      const err = new Error('PASSWORD_RESET_REQUIRED');
      err.challenge = 'PASSWORD_RESET_REQUIRED';
      err.email = data.email;
      throw err;
    }

    const me = { email, name: data.login?.name || email, loginId: data.login?.id };
    sessionStorage.setItem('accessToken', data.accessToken);
    if (data.cognitoIdToken) sessionStorage.setItem('cognitoIdToken', data.cognitoIdToken);
    sessionStorage.setItem('me', JSON.stringify(me));
    sessionStorage.setItem('auth', `com.endofscoring.cognito|${email}`);
    setUser(me);
    return me;
  }, []);

  const forceChangePassword = useCallback(async ({ email, newPassword, session }) => {
    const api = await import('../api.js');
    const { data } = await api.default.post('/api/logins/force-change-password', { email, newPassword, session });
    const me = { email, name: data.login?.name || email, loginId: data.login?.id };
    sessionStorage.setItem('accessToken', data.accessToken);
    if (data.cognitoIdToken) sessionStorage.setItem('cognitoIdToken', data.cognitoIdToken);
    sessionStorage.setItem('me', JSON.stringify(me));
    sessionStorage.setItem('auth', `com.endofscoring.cognito|${email}`);
    setUser(me);
    return me;
  }, []);

  const forgotPassword = useCallback(async ({ email }) => {
    const api = await import('../api.js');
    await api.default.post('/api/logins/forgot-password', { email });
  }, []);

  const confirmForgotPassword = useCallback(async ({ email, code, newPassword }) => {
    const api = await import('../api.js');
    const { data } = await api.default.post('/api/logins/confirm-forgot-password', { email, code, newPassword });
    const me = { email, name: data.login?.name || email, loginId: data.login?.id };
    sessionStorage.setItem('accessToken', data.accessToken);
    if (data.cognitoIdToken) sessionStorage.setItem('cognitoIdToken', data.cognitoIdToken);
    sessionStorage.setItem('me', JSON.stringify(me));
    sessionStorage.setItem('auth', `com.endofscoring.cognito|${email}`);
    setUser(me);
    return me;
  }, []);

  const signInWithGoogle = useCallback(async ({ idToken }) => {
    // Best-effort parse of JWT payload for display. The server does the real
    // verification, so don't block sign-in if atob/base64url parsing fails.
    let email = '';
    let name = '';
    try {
      const b64url = idToken.split('.')[1];
      const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
      const json = decodeURIComponent(
        atob(padded).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''),
      );
      const payload = JSON.parse(json);
      email = payload.email || '';
      name = payload.name || payload.email || '';
    } catch { /* fall back to empty — server still validates */ }
    const me = { email, name };
    sessionStorage.setItem('accessToken', `com.google|${idToken}`);
    sessionStorage.setItem('me', JSON.stringify(me));
    if (email) sessionStorage.setItem('auth', `com.google|${email}`);
    setUser(me);
    return me;
  }, []);

  const signUp = useCallback(async ({ email, password, name }) => {
    const api = await import('../api.js');
    const { data } = await api.default.post('/api/logins/signup', { email, password, name: name || email });
    const me = { email, name: data.login?.name || email, loginId: data.login?.id };
    sessionStorage.setItem('accessToken', data.accessToken);
    if (data.cognitoIdToken) sessionStorage.setItem('cognitoIdToken', data.cognitoIdToken);
    sessionStorage.setItem('me', JSON.stringify(me));
    sessionStorage.setItem('auth', `com.endofscoring.cognito|${email}`);
    setUser(me);
    return me;
  }, []);

  const signOut = useCallback(() => {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('me');
    sessionStorage.removeItem('auth');
    setUser(null);
  }, []);

  const value = { user, loading, signIn, signUp, signInWithGoogle, signOut, forceChangePassword, forgotPassword, confirmForgotPassword, isAuthenticated: !!user };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export default AuthContext;
